import api from '@/lib/services/general.service'
import { buildComparePrompt, canCompare, comparisonTitle, picksKey, togglePick } from '@/lib/packCompare'
import {
  PDF_CONTENT_TYPE,
  PDF_PROVIDER,
  buildComparisonPdf,
  comparisonPdfChip,
  comparisonPdfFilename,
  pdfFromDocuments,
  uploadComparisonPdf,
} from '@/lib/comparisonPdf'

// The pack-vs-pack comparison journey (Research Packs epic, frames 05-12): the user PICKS two or
// more Research Packs from the story's pack list, RUNS the comparison (one AI web ask built from
// every picked pack's saved research) and SAVES the result into ALL of them — as ONE shared
// research item on the server, never a copy, so the packs stay in sync.
export const state = {
  picks: [], // [{ id, title }] — two or more packs; uncapped (Pouria, 2026-08-16)
  ongoingId: null, // the story whose packs are being compared
  status: 'idle', // 'idle' | 'running' | 'done' | 'error'
  title: '', // e.g. "Prada vs Louis Vuitton"
  answer: '', // the AI's markdown result (table + recommendation)
  sources: [], // [{ title, url }] — the web sources behind the answer
  saving: false,
  // Has the answer on screen been FILED into its packs (2026-09-15 parity audit E-7,
  // busy-my-web#488)? The comparison used to be reset and the page left the moment the save landed;
  // the frames (40006859:277503) keep it on screen with a "Saved to research packs… View ✕" receipt,
  // so the store has to remember that this particular answer is already filed — otherwise the card
  // would keep offering to save it again.
  saved: false,
  // Which verb the picks card's radio has chosen — read by the SmartBox action row, whose pill
  // says 'Compare' or 'Review' accordingly (the buttons live there, not on the card).
  pickAction: 'compare', // 'compare' | 'review'
  // The picks card's ANSWER for one selection (busy-my-web#667), keyed by `picksKey`: which
  // selection "Compare Research Packs" was chosen on (the action row's Compare pill shows only
  // then), and which one the card was closed on (the pill goes with it; the picks stay). Kept here,
  // not on the card, because the card and the action row are two components reading one answer.
  chosenKey: null,
  dismissedKey: null,
  // Which RUN the store is showing (2026-09-15 parity audit E-5, busy-my-web#487). The comparison
  // is a one-shot POST, not a stream, so its blue stop button cannot close a connection — it
  // abandons the run instead: the token moves, and the answer that eventually arrives finds itself
  // stale and is dropped. Without it a stopped comparison would repaint itself seconds later.
  runId: 0,
  // THE COMPARISON'S PDF (busy-my-web#506). "Create PDF" used to open a print window, so nothing
  // was ever produced and nothing could be pointed at afterwards. It now renders a real PDF blob,
  // stores it the way an attachment is stored, and remembers the record here:
  //
  //  • `itemId`  — the SAVED comparison's own research-item id, resolved off the story after the
  //                save lands. It is the key the file is filed under, which is what lets the rail
  //                find the PDF of a comparison saved days ago (`pdfs` below).
  //  • `pdf`     — what the last Create PDF produced, for the receipt's chip on THIS screen.
  //  • `pdfs`    — { [comparison item id]: chip }, the map every rail step reads. Filled by
  //                `createPdf` and by `loadPdf`, which asks the document list for a comparison the
  //                user is only looking at.
  //  • `pdfBusy` — a PDF is being made right now; the card's button is disabled and the strip spins.
  itemId: null,
  pdf: null,
  pdfs: {},
  pdfBusy: false,
}

export const getters = {
  picks: s => s.picks,
  count: s => s.picks.length,
  isPicked: s => id => s.picks.some(p => p.id === id),
  active: s => s.status !== 'idle',
  status: s => s.status,
  /** Is a comparison being made right now? — what puts the stop button in the box (#487). */
  running: s => s.status === 'running',
  title: s => s.title,
  answer: s => s.answer,
  sources: s => s.sources,
  saving: s => s.saving,
  /** Already filed into its packs — the receipt state (#488). */
  saved: s => s.saved,
  ongoingId: s => s.ongoingId,
  pickAction: s => s.pickAction,
  chosenKey: s => s.chosenKey,
  dismissedKey: s => s.dismissedKey,
  /** The saved comparison's research-item id — what its PDF is filed under (#506). */
  itemId: s => s.itemId,
  /** The PDF this screen has just made, for the receipt's chip (#506). */
  pdf: s => s.pdf,
  /** Every comparison's PDF the app knows of, keyed by the comparison's item id (#506). */
  pdfs: s => s.pdfs,
  /** Is one being made right now? (#506) */
  pdfBusy: s => s.pdfBusy,
  /** The chip for one comparison, or null — what a rail step draws (#506). */
  pdfFor: s => id => (id ? s.pdfs[id] || null : null),
}

// How long a comparison save may go unanswered before it is called a failure (#152). Generous —
// the endpoint writes one shared item, its junction rows and a timeline event, and the user is
// watching a spinner the whole time — but FINITE, which is the entire point: without a deadline a
// hung request spins for as long as the tab is open.
export const SAVE_TIMEOUT_MS = 20000

// `Promise.race` with a timer, and the timer is always cleared: a save that answers at 19s must not
// leave a 20s timeout alive to reject into nothing (an unhandled rejection in every later test run).
export function withDeadline(promise, ms = SAVE_TIMEOUT_MS) {
  let timer = null
  const deadline = new Promise((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error('comparison save timed out')), ms)
  })
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer))
}

/**
 * WHICH research item a just-saved comparison became (busy-my-web#506).
 *
 * The save endpoint answers with the whole story, so the item is identified the way the user would:
 * the comparison with this title, the newest one if the same comparison has been filed twice.
 * Exported so it can be tested without a store around it.
 *
 * @param {object} story the refreshed ongoing story
 * @param {string} title the comparison's title
 * @returns {string|null}
 */
export function findComparisonItemId(story, title) {
  const wanted = String(title || '').trim().toLowerCase()
  const items = (story?.research || [])
    .filter(r => r?.kind === 'comparison' && String(r.title || '').trim().toLowerCase() === wanted)
  if (!items.length) return null
  const newest = items.reduce((a, b) => (new Date(b.savedDate || 0) > new Date(a.savedDate || 0) ? b : a))
  return newest.id || null
}

export const actions = {
  // Tick/untick a pack card (frame 05). Every ticked pack joins the comparison — a third,
  // fourth… pack is a wider comparison, not an ignored pick.
  toggle({ commit, state: s }, pack) { commit('SET_PICKS', togglePick(s.picks, pack)) },
  clearPicks({ commit }) { commit('SET_PICKS', []) },
  // EVERY WAY OUT of a pack selection (busy-my-web#671, Pouria: "if we close the compare or go back
  // we need to clear the selection as well") — the compare card's x, the gutter's Back / Close and
  // a breadcrumb walked back out of the packs level all land here, so they cannot each forget a
  // different part of it. It drops the ticks (and with them the Work-board chips, the Briefing's
  // "Selected" and the count), the card's answer for them (`chosenKey` — the pending Compare pill,
  // and `dismissedKey`), the radio, and the pack the Briefing selected (`storyFilters.packId`, the
  // other half of the same gesture — briefing/selectPack ticks the pack as it selects it). The kind
  // filter is left as it stands: which LEVEL the user is on is the caller's step, not the
  // selection's. A comparison already RUN is not touched — that is `reset`, the result page's own.
  clearSelection({ commit, dispatch, rootGetters }) {
    commit('SET_PICKS', [])
    commit('SET_CHOSEN_KEY', null)
    commit('SET_DISMISSED_KEY', null)
    commit('SET_PICK_ACTION', 'compare')
    if (rootGetters?.['ongoing/storyFilters']?.packId) dispatch('ongoing/setStoryFilters', { packId: null }, { root: true })
  },
  // Seed the picks from somewhere other than the pack cards' ticks — the open-pack suggestion
  // (Pouria, 2026-08-18) accepts by writing the packs it offered in here, so from that point on
  // the comparison runs down the one ticked path instead of a parallel one.
  setPicks({ commit }, packs) {
    commit('SET_PICKS', (packs || [])
      .filter(p => p && p.id)
      .map(p => ({ id: p.id, title: p.title || 'Research pack' })))
  },
  setPickAction({ commit }, v) { commit('SET_PICK_ACTION', v === 'review' ? 'review' : 'compare') },
  // The picks card's two answers (#667), each about the selection whose key is passed.
  choose({ commit }, key) { commit('SET_CHOSEN_KEY', key || null) },
  dismiss({ commit }, key) { commit('SET_DISMISSED_KEY', key || null) },

  // Run the comparison (frame 08): one AI web ask over EVERY picked pack's saved research.
  async start({ commit, state: s, rootGetters }, ongoingId) {
    const research = rootGetters['ongoing/currentStory']?.research || []
    if (!ongoingId || s.status === 'running' || !canCompare(s.picks, research).ok) return false
    commit('START', { ongoingId, title: comparisonTitle(s.picks) })
    const run = s.runId
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const res = await api.insert('ai/ask', { question: buildComparePrompt(s.picks, research), web: true, timeZoneId })
      const data = res?.data?.data ?? res?.data ?? {}
      // Stopped while it ran (#487): the run this answer belongs to is no longer the one on screen,
      // so it is dropped rather than repainting a comparison the user has closed.
      if (s.runId !== run) return true
      commit('DONE', { answer: data.answer || '', sources: Array.isArray(data.webSources) ? data.webSources : [] })
    } catch {
      if (s.runId === run) commit('FAIL')
    }
    return true
  },

  /**
   * STOP the comparison being made (2026-09-15 parity audit E-5, busy-my-web#487).
   *
   * Frame 40006822:264783 draws the same blue square over "Comparing…" that a search gets over
   * "Searching…". A comparison is one request, not a stream, so there is no connection to close:
   * the run is abandoned — the card goes back to the picks it was started from, and the answer
   * that arrives later is dropped by the run token above.
   */
  stop({ commit, state: s }) {
    if (s.status !== 'running') return false
    commit('STOP')
    return true
  },

  // Save the result into ALL the picked packs (frames 10-12): one shared research item referenced
  // by every pack — the server enforces the at-least-two rule. Refreshes the story so each pack
  // view and the ongoing reflect it (frames 13-15).
  //
  // #152 (Saffet): "it keeps going round and round trying to save something." `api.insertWithPath`
  // is a bare `axios.post` and the app configures NO axios timeout anywhere, so a request the
  // server never answers — a hung connection, a dropped socket, a 502 that never arrives — leaves
  // this promise pending FOREVER. `saving` then stays true, the box's strip keeps its spinner, and
  // the journey has no end: the save neither completes nor reports failure.
  //
  // Two guards, because the report did not say whether the save lands (Saffet never answered):
  //  • the DOUBLE-SUBMIT guard is the `s.saving` bail above — already there, kept explicit here
  //    because it is now half of the contract rather than an incidental optimisation;
  //  • the DEADLINE below is the new half. A save that has not answered inside SAVE_TIMEOUT_MS is
  //    treated as failed and says so, which is the only honest thing to show: the request may yet
  //    land on the server, so the notice offers a retry rather than claiming the work was lost, and
  //    the story refresh that follows a successful save is exactly what would reveal it.
  //
  // Only the POST is raced. The story refresh after it is best-effort — `ongoing/getStory` swallows
  // its own errors and the save has already happened by then, so a slow refresh must not turn a
  // landed comparison into a reported failure.
  async save({ commit, state: s, dispatch, rootGetters, rootState }) {
    if (s.status !== 'done' || !s.ongoingId || s.saving) return false
    commit('SET_SAVING', true)
    // THE WRITE. This is the only part whose failure means the comparison was not saved.
    try {
      await withDeadline(api.insertWithPath('ongoing', `${s.ongoingId}/packs/comparison`, {
        packIds: s.picks.map(p => p.id),
        title: s.title,
        content: s.answer,
      }))
    } catch {
      commit('SET_SAVING', false)
      return false
    }
    // …and past this line the comparison EXISTS. The refresh that follows only catches the views
    // up (frames 13-15), so its failure is not the save's failure: reporting "could not save" over
    // a comparison that is sitting on the server is the worst of both answers — it sends the user
    // to save it a second time. The spinner stays up while it runs, so "Saved" is not announced
    // over a story that has not caught up yet.
    try {
      await dispatch('ongoing/getStory', s.ongoingId, { root: true })
      dispatch('ongoing/fetch', null, { root: true }) // sidebar order/status may have moved
    } catch { /* the story catches up by itself on the next read */ }
    commit('SET_SAVING', false)
    commit('SET_SAVED', true)
    // WHICH research item the comparison became (#506). The endpoint answers with the whole story
    // rather than the item, and the refresh above has just put that story in the ongoing module —
    // so the item is found there, by the kind and title that were just written. It is the key the
    // PDF is filed under, so a comparison saved today and a comparison saved last week both point
    // their chip at the same place.
    const itemId = findComparisonItemId(rootGetters?.['ongoing/currentStory'], s.title)
    commit('SET_ITEM_ID', itemId)
    // A PDF made BEFORE the save was filed under the ongoing, because the comparison had no item
    // of its own to be filed under yet (#506). Now it has one — so the same stored file is linked
    // a second time, under the comparison, and the rail finds it on every later visit. Best-effort:
    // the file exists either way, and a failed second link costs the chip, not the document.
    if (itemId && s.pdf) {
      try {
        await api.addDocument({
          provider: PDF_PROVIDER,
          userId: rootState?.user?.userId,
          timelineId: itemId,
          externalEntityId: String(s.pdf.fileId || s.pdf.id),
          externalId: String(s.pdf.fileId || s.pdf.id),
          filename: s.pdf.filename,
          contentType: s.pdf.contentType || PDF_CONTENT_TYPE,
          octets: 0,
          hasThumbnail: false,
        })
        commit('SET_PDF', { key: itemId, chip: s.pdf })
      } catch { /* the chip is lost, the file is not */ }
    }
    return true
  },

  /**
   * CREATE THE PDF (busy-my-web#506, frames 40006859:277503 and 40008216:411421).
   *
   * Three steps, in this order, and every one of them can be the failure the card reports:
   * render the blob, upload it the way an attachment is uploaded, remember the record. The
   * comparison is NOT re-saved here — a PDF of an unfiled comparison is still a PDF the user asked
   * for — but where the comparison HAS been filed the file is keyed on its item so the rail can
   * find it again; where it has not, the ongoing's own id is the key, and the receipt on screen is
   * the only place the chip appears. That asymmetry is the frames': the chip they draw sits under
   * "Comparison saved".
   *
   * @returns {Promise<object|null>} the chip, or null when nothing was made
   */
  async createPdf({ commit, state: s, rootState }) {
    if (s.status !== 'done' || s.pdfBusy) return null
    const key = s.itemId || s.ongoingId
    if (!key) return null
    commit('SET_PDF_BUSY', true)
    try {
      const filename = comparisonPdfFilename(s.title)
      const blob = await buildComparisonPdf({ title: s.title, answer: s.answer, sources: s.sources })
      const doc = await uploadComparisonPdf(api, {
        blob,
        filename,
        userId: rootState.user?.userId,
        timelineId: key,
      })
      const chip = comparisonPdfChip(doc)
      commit('SET_PDF', { key, chip })
      return chip
    } catch (err) {
      console.error('[comparison] create pdf failed', err)
      return null
    } finally {
      commit('SET_PDF_BUSY', false)
    }
  },

  /**
   * Has this comparison got a PDF? — asked once per comparison the rail is drawing (#506).
   *
   * Best-effort and quiet: a story with no documents answers an empty list, and a failure simply
   * leaves the step without a chip, which is what a step without a PDF looks like anyway.
   */
  async loadPdf({ commit, state: s }, itemId) {
    if (!itemId || Object.prototype.hasOwnProperty.call(s.pdfs, itemId)) return null
    // Claimed before the request so a rail of five comparisons asks once each, not once per repaint.
    commit('SET_PDF', { key: itemId, chip: null })
    try {
      const res = await api.getDocumentsByTimeline(itemId)
      const list = res?.data?.thumbnails || res?.data?.data || res?.data || []
      const chip = pdfFromDocuments(list)
      if (chip) commit('SET_PDF', { key: itemId, chip })
      return chip
    } catch {
      return null
    }
  },

  reset({ commit }) { commit('RESET') },
}

export const mutations = {
  // A DIFFERENT selection is a new question (#667): the card comes back and the pill waits for it
  // again. Writing the same packs (accepting the open-pack suggestion seeds exactly what it offered)
  // keeps the answer.
  SET_PICKS(s, v) {
    if (picksKey(v) !== picksKey(s.picks)) {
      s.chosenKey = null
      s.dismissedKey = null
    }
    s.picks = v
  },
  SET_CHOSEN_KEY(s, v) { s.chosenKey = v },
  SET_DISMISSED_KEY(s, v) { s.dismissedKey = v },
  START(s, { ongoingId, title }) {
    s.ongoingId = ongoingId
    s.title = title
    s.status = 'running'
    s.answer = ''
    s.sources = []
    s.saved = false
    s.itemId = null
    s.pdf = null
    s.runId += 1
  },
  // Abandon the run in flight (#487): back to idle, and the token moved so its answer is stale.
  STOP(s) {
    s.status = 'idle'
    s.title = ''
    s.answer = ''
    s.sources = []
    s.saved = false
    s.itemId = null
    s.pdf = null
    s.runId += 1
  },
  DONE(s, { answer, sources }) {
    s.status = 'done'
    s.answer = answer
    s.sources = sources
  },
  FAIL(s) { s.status = 'error' },
  SET_SAVING(s, v) { s.saving = v },
  SET_SAVED(s, v) { s.saved = !!v },
  SET_ITEM_ID(s, v) { s.itemId = v || null },
  SET_PDF_BUSY(s, v) { s.pdfBusy = !!v },
  // One write for both readers: the map the rail reads and, when something was actually produced,
  // the receipt's own record. A `null` chip is a REMEMBERED "asked, nothing there" — it must not
  // wipe the PDF the card is showing.
  SET_PDF(s, { key, chip }) {
    s.pdfs = { ...s.pdfs, [key]: chip }
    if (chip) s.pdf = chip
  },
  SET_PICK_ACTION(s, v) { s.pickAction = v },
  RESET(s) {
    s.picks = []
    s.ongoingId = null
    s.status = 'idle'
    s.title = ''
    s.answer = ''
    s.sources = []
    s.saving = false
    s.saved = false
    s.pickAction = 'compare'
    s.chosenKey = null
    s.dismissedKey = null
    s.runId += 1
    s.itemId = null
    s.pdf = null
    s.pdfBusy = false
    // `pdfs` SURVIVES the reset: it is what the app knows about comparisons in general (every rail
    // step reads it), not what this screen is doing. Clearing it would blank the chips on the
    // story the user lands on.
  },
}
